import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import ModalService from "src/modals"
import { selectGameConfig } from "src/store/common"
import { selectGame, selectViewerId } from "src/store/game"
import { useScrollEdges } from "src/utils/scrollEdges"
import { foodIcon } from "src/utils/resources"
import { maxActions as maxActionsFor } from "src/utils/gameModifiers"
import FoodOutlook from "src/components/FoodOutlook"
import ResearchStatus from "src/components/ResearchStatus"
import StandingOrders from "src/components/StandingOrders"

/* The resource chips of the top bar: food or meat with next round's outlook, research, the action
   budget, the standing orders — and, for a player, the battle plan.

   `chips` is which of them to draw, and it exists for the phone (owner, 2026-09-09: "the remaining
   actions in the round should be in the left top over the map as a counter. It does not fit in the
   top bar. Same for the troop queue and whatever other button appears there that is not the
   food/meat"). At 390px the row held one chip of five and swallowed the other four behind a fade,
   and one of the four was the action budget — the number that decides what a player may do this
   round. So on that shell the bar keeps the resource and the rest move onto the map, which has
   room: `"resource"` in the bar, `"rest"` over the board, `"all"` (the default) everywhere else.

   One component either way rather than a phone-only copy: a second row of chips would be a second
   place for a number to be wrong, which is the reason this was lifted out of `Game.js` to begin
   with.

   They read the *viewer's* seat (`selectViewerId`), which is what lets the same row answer for a
   player, for a spectator watching through somebody, and for a replay (owner's ask, 2026-09-06:
   "in the replay view we want to see the meat and all the different stuff the user is seeing").
   Lifted out of `Game.js` for that last one: a second copy of this row would be a second place for
   a number to be wrong.

   `canEditFormation` is the one chip that writes rather than reads — not offered to a watcher,
   because it writes to a team that is not theirs. */
const TopBarStats = ({ canEditFormation = false, chips = "all" }) => {
  const { t } = useTranslation()
  const game = useSelector(selectGame)
  const userId = useSelector(selectViewerId)
  const gameConfig = useSelector(selectGameConfig)
  const [statsRef, statsEdges] = useScrollEdges()

  const playerMetadata = game?.metadata?.users?.[userId]
  if (!playerMetadata) return null

  const maxActions = maxActionsFor({ gameConfig, race: playerMetadata.race })
  const actionsUsed = playerMetadata.actions_used ?? 0
  const actionsRemaining = maxActions - actionsUsed

  const showsResource = chips !== "rest"
  const showsRest = chips !== "resource"

  return (
    <div className="topbar-stats" ref={statsRef} data-scroll={statsEdges || undefined}>
      {showsResource && (
        <div
          className="stat-chip food-chip stat-chip-pressable"
          role="button"
          tabIndex={0}
          onClick={() => ModalService.show("StatDetailModal", { topic: "food" })}
        >
          <div className="stat-chip-main">
            {/* Each side's stockpile wears the same glyph as every price, upkeep and battle bill
                on screen — 🥩 for the horde (owner, 2026-09-09), 🌽 for humanity (owner,
                2026-09-10, replacing a drawn crop) — so the number in the top bar and the number
                on the button a player is about to press are plainly the same coin. `foodIcon` is
                where both live. */}
            <span className="resource-icon glyph" role="img" aria-label={t("game.topBar.foodAlt")}>
              {foodIcon(playerMetadata.team)}
            </span>
            <p className="stat-chip-value">{playerMetadata.food}</p>
          </div>
          {/* Income minus upkeep. The stockpile alone says nothing about direction, and starvation
              takes the whole army in one tick — see requirement 18d. */}
          <FoodOutlook />
        </div>
      )}
      {showsRest && (
        <>
          {/* Research points and the action budget both existed on the wire and appeared nowhere.
              The budget lived only inside the Moves tab, so a player learned their limit by opening
              a tab about troop movements. The research chip also says when the number cannot move
              yet, because its building starts at level 0 — see requirement 18d. */}
          <ResearchStatus />
          <div
            className={`stat-chip actions-chip stat-chip-pressable${actionsRemaining > 0 ? " stat-chip-amber" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => ModalService.show("StatDetailModal", { topic: "actions" })}
          >
            <p className="stat-chip-value" title={t("game.topBar.actionsTitle")}>
              ⚡ {actionsUsed} / {maxActions}
            </p>
            <p className="stat-chip-caption">{t("game.topBar.actions")}</p>
          </div>
          {/* A production queue is a standing order. Playing to tick 24 showed what that costs when
              it is only visible inside a city modal — see requirement 18d. */}
          <StandingOrders />
          {/* The battle plan, editable before any battle starts: the plan is the team's and
              applies to every fight, so it should not take a battle in progress to reach it. */}
          {canEditFormation && (
            <div
              className="stat-chip stat-chip-pressable"
              role="button"
              tabIndex={0}
              onClick={() => ModalService.show("FormationModal")}
              title={t("game.formation.title")}
            >
              <p className="stat-chip-value">✎</p>
              <p className="stat-chip-caption">{t("game.formation.chip")}</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default TopBarStats
